<template>
  <section
    data-test="pitch-type-card"
    class="pitchtype-details"
    :class="[
      {
        desktop: $isDesktop,
        unavailable: pitchNotAvailable,
        'date-range-selected': hasChargeType && !onlyArriveSelected,
        'min-nights':
          chargetype &&
          chargetype.state === 'min_nights' &&
          !chargeTypesUpdating &&
          !onlyArriveSelected,
      },
    ]"
  >
    <section class="pt-wrapper">
      <header class="pt-header" :class="{ scrolled: ptBodyScrolled }">
        <pitchtype-detail-close />
        <pitch-type-summary
          v-if="!chargeTypesUpdating"
          :pitchtype="pitchtype"
          :pitchtype-index="1"
          :show-pitchtype-details="true"
          @click="scrollAvailabilitySectionIntoView()"
        >
          <template #state>
            <charge-type-state v-if="hasChargeType" :pitchtype="pitchtype" />
            <pitch-type-state
              v-if="!hasChargeType && !chargeTypesUpdating"
              :pitchtype="pitchtype"
            />
          </template>

          <template #status-msg>
            <pitch-type-status-msg
              :is-available="isAvailable"
              :has-charge-type="hasChargeType"
              :is-low-availability="isLowAvailability"
              :offers-active="offersActive"
              :show-offers-losenge="showOffersLosenge"
              :offer-percentage="offerPercentage"
              :pitches-to-sell="pitchesToSell"
            />
          </template>
        </pitch-type-summary>
      </header>

      <section class="pt-body">
        <div
          v-if="$isDesktop"
          v-observe-visibility="{
            callback: headerVisibilityChanged,
            throttle: 300,
            throttleOptions: {
              leading: 'hidden',
            },
          }"
        />
        <pitch-type-detail
          :campsite-super-facilities="campsite.superFacilities"
          :pitchtype="pitchtype"
          :show-pitchtype-details="true"
        >
          <template #availability>
            <pitchtype-details-availability-section
              data-nosnippet
              :available-to-search="campsite.availableToSearch"
              :campsite-opening-dates-range="campsiteOpeningDatesRange"
              :campsite-slug="campsite.slug"
              :campsite-categories="campsite.categories"
              :pitchtype-id="pitchtype.id"
              :dates="selectedDates"
              :party="party"
              :should-show-availability-details="shouldShowAvailabilityDetails"
              :calendar-start-date="campsite.calendarStartDate"
              :selected-dates-price="selectedDatesPrice"
              :show-asterix="!!pitchtype.excludedPrice"
              @datesChanged="$emit('datesChanged', $event)"
              @availability-details-changed="
                shouldShowAvailabilityDetails = $event
              "
              @rangeChanged="onChangeRange"
            />
            <availability-error-state
              v-if="showAvailabilityErrorState"
              class="availability-error-state"
              :chargetype="pitchtype.chargetype"
              :capacity="pitchtype.capacity"
            />
            <template v-if="!campsite.availableToSearch && !hasChargeType">
              <div class="availability-error-state">
                <img
                  loading="lazy"
                  src="@/assets/images/warning-red.svg"
                  width="35"
                  height="35"
                  data-test="warning-red-1"
                />
                <translate>
                  No availability at present on Pitchup.com
                </translate>
              </div>
              <div class="zero-availability">
                <p>
                  <i18n-t
                    keypath="{chooseYourDates} and we'll email when available."
                  >
                    <template #chooseYourDates>
                      <a @click.prevent.stop="onCheckAvailability">
                        <translate>Choose your dates</translate>
                      </a>
                    </template>
                  </i18n-t>
                  <NuxtLink
                    role="button"
                    class="nearby-link"
                    :to="{
                      query: { ...$route.query, pitchtype: undefined },
                      hash: '#nearby-bookable',
                    }"
                  >
                    <translate>See nearby bookable sites</translate>
                  </NuxtLink>
                </p>
              </div>
            </template>
            <template
              v-if="
                !hasChargeType &&
                campsite.availableToSearch &&
                partyValidation.isOverCapacity
              "
            >
              <div class="availability-error-state">
                <img
                  loading="lazy"
                  src="@/assets/images/warning-red.svg"
                  width="35"
                  height="35"
                  data-test="warning-red-party-capacity"
                />
                <translate v-if="partyValidation.violation === 'adults_only'">
                  Adults only
                </translate>
                <translate
                  v-else-if="partyValidation.violation === 'humans'"
                  :tt="{ amount: partyValidation.capacityDetails?.maxPeople }"
                >
                  {amount, plural, one{# person maximum} other{# people
                  maximum}}
                </translate>
                <translate
                  v-else-if="
                    partyValidation.violation === 'dogs' &&
                    partyValidation.capacityDetails?.maxDogs === 0
                  "
                >
                  Dogs not allowed
                </translate>
                <translate
                  v-else-if="partyValidation.violation === 'dogs'"
                  :tt="{ amount: partyValidation.capacityDetails?.maxDogs }"
                >
                  Exceeds max. {amount, plural, one{dog} other{dogs}} ({amount})
                </translate>
                <translate v-else> Party size exceeds capacity </translate>
              </div>
            </template>
          </template>

          <template #costs-breakdown>
            <costs-breakdown
              v-if="hasChargeType"
              :campsite="costsBreakdownCampsiteData"
              :start="start"
              :end="end"
              :party="party"
              :city-tax="campsite.cityTax"
              :pitchtype="pitchtype"
              :charge-types-updating="chargeTypesUpdating"
              :age-restrictions="pitchtype.ageRestrictions"
              @check-availability="onCheckAvailability(true)"
              @clearDates="clearDates"
            />
            <default-costs-breakdown
              v-else-if="campsite.availableToSearch"
              data-nosnippet
              :campsite="costsBreakdownCampsiteData"
              :date-range="selectedDates"
              :party="party"
              :party-validation="partyValidation"
              :pitchtype="pitchtype"
              :age-restrictions="pitchtype.ageRestrictions"
              @check-availability="onCheckAvailability"
              @clearDates="clearDates"
            />
          </template>

          <template #extras>
            <extras
              v-if="shouldShowExtras"
              :extras="chargetype.extras"
              class="extras-table dated-extras"
            >
              <template #extra="{ extra }">
                <extra-dated :extra="extra" />
              </template>
            </extras>

            <template v-else>
              <extras
                v-if="$isDesktop"
                data-nosnippet
                :extras="pitchtype.extras"
                class="extras-table"
              >
                <template #extra="{ extra }">
                  <extra-non-dated :extra="extra" />
                </template>
              </extras>

              <div v-else class="undated-extras-accordion" data-nosnippet>
                <ui-collapse
                  v-if="pitchtype.extras.length"
                  :collapse-id="`collapse-extras-${pitchtype.id}`"
                >
                  <template #ui-collapse-title>
                    <translate> Extras </translate>
                  </template>
                  <template #ui-collapse-body>
                    <extras :extras="pitchtype.extras" class="extras-table">
                      <template #extra="{ extra }">
                        <extra-non-dated :extra="extra" />
                      </template>
                    </extras>
                  </template>
                </ui-collapse>
              </div>
            </template>
          </template>

          <template #notify-me-above>
            <notify-me
              v-show="shouldShowNotifyMe && !alternativeStaysData.length"
              :campsite-name="campsite.name"
              :pitchtypes="[pitchtype]"
              :date-range="dateRange"
              :party="party"
              class="pt-notify-me"
              data-test="notify-me-above"
            />
          </template>

          <template #notify-me-below>
            <notify-me
              v-show="shouldShowNotifyMe && alternativeStaysData.length"
              :campsite-name="campsite.name"
              :pitchtypes="[pitchtype]"
              :date-range="dateRange"
              :party="party"
              class="pt-notify-me"
              data-test="notify-me-below"
            />
          </template>
        </pitch-type-detail>
      </section>

      <footer class="pt-footer" data-nosnippet>
        <nuxt-link
          role="button"
          class="button sml minimal close-btn"
          :to="{
            query: { ...$route.query, pitchtype: undefined },
            force: true,
          }"
        >
          <translate>Close</translate>
        </nuxt-link>
        <pitchtype-booking-link
          v-if="hasChargeType"
          v-bind="pitchtypeBookingLinkProps"
          class="footer-action-button"
        />
        <pitchtype-edit-availability-search-button
          v-else
          class="footer-action-button"
          :class="{ disabled: !campsite.availableToSearch }"
          @click="onCheckAvailability"
        />
      </footer>
    </section>
  </section>
</template>

<script lang="ts">
import { isEqual } from 'lodash-es'
import { computed, provide } from 'vue'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { scrollIntoView } from '~/utility/scroll'
import { logger } from '../../../utility/logger'
import { CalendarDay, fetchAlternativeStays } from '../alternativeStaysFetch'
import { validatePitchtypeCapacity } from '~/utility/campsite/party-validation'
import type {
  CampsiteDetails,
  OpeningDatesRange,
  PitchTypeWithChargeType,
  RangeChangedEvent,
} from '../types'
import type { Party } from './types'

@Component({
  emits: ['rangeChanged', 'datesChanged'],
})
class CampsitePitchtypeDetailMain extends Vue {
  @Prop({ required: true })
  campsite: CampsiteDetails

  @Prop({ required: true })
  pitchtype: PitchTypeWithChargeType

  @Prop({ required: true })
  party: Party

  @Prop({ required: true })
  chargeTypesUpdating: boolean

  @Prop({ required: true })
  hasAvailability: boolean | null

  @Prop({ required: false, default: () => ({ start: null, end: null }) })
  dateRange: { start?: string; end?: string }

  @Prop()
  campsiteOpeningDatesRange: OpeningDatesRange[]

  @Prop({ required: false, default: () => [] })
  categoryIds: string[]

  @Watch('dateRange', { deep: true })
  async dateRangeChanged(newRange, oldRange) {
    if (this.$.isUnmounted) return

    logger('dateRange changed', newRange, oldRange)
    this.selectedDates = {
      start: newRange.start || '',
      end: newRange.end || '',
    }
    // don't scroll if setting dates for first time
    if (!oldRange.start || !this.$isDesktop || this.$.isUnmounted) {
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 500))
    if (process.client && !this.$.isUnmounted) {
      scrollIntoView('.total-stay')
    }
  }

  shouldShowAvailabilityDetails = false

  created() {
    if (this.hasChargeType && this.pitchNotAvailable) {
      this.shouldShowAvailabilityDetails = false
    } else {
      this.shouldShowAvailabilityDetails = !this.hasAvailability
    }
    this.selectedDates = {
      start: this.dateRange.start || '',
      end: this.dateRange.end || '',
    }
    logger('creating alternativeStays inject')
    this.provideAlternativeStays()
  }

  async mounted() {
    await this.checkChargetypeState()
  }

  async onCheckAvailability(forceScroll: boolean = false) {
    this.shouldShowAvailabilityDetails = true
    if (this.hasChargeType && !forceScroll) {
      return
    }
    await this.scrollAvailabilitySectionIntoView()
  }

  async scrollAvailabilitySectionIntoView() {
    await this.$nextTick()
    if (process.client) {
      scrollIntoView('.flatpickr-innerContainer', { block: 'start' })
    }
  }

  selectedDates = { start: '', end: '' }
  get start() {
    return this.selectedDates.start
  }

  get end() {
    return this.selectedDates.end
  }

  get onlyArriveSelected() {
    return !!this.selectedDates.start && !this.selectedDates.end
  }

  get showAvailabilityErrorState() {
    return (
      (this.hasChargeType &&
        !this.onlyArriveSelected &&
        !this.alternativeStaysData.length) ||
      this.chargetype?.isOvercapacityMessage
    )
  }

  async onChangeRange(dates: RangeChangedEvent) {
    if (
      this.selectedDates.start !== dates.arrive ||
      this.selectedDates.end !== dates.depart
    ) {
      this.selectedDates = { start: dates.arrive, end: dates.depart }
      if (!this.onlyArriveSelected) {
        this.$emit('rangeChanged', dates)
        await this.$nextTick()
        const calendarHeader = document.querySelector(
          '.flatpickr-weekdays',
        ) as HTMLElement
        if (calendarHeader) {
          calendarHeader.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          })
        }
      }
    }
  }

  clearDates() {
    this.selectedDates = { start: '', end: '' }
  }

  get hasChargeType() {
    return !!this.chargetype && !this.chargeTypesUpdating
  }

  get chargetype() {
    return this.pitchtype?.chargetype
  }

  get selectedDatesPrice() {
    return !this.chargeTypesUpdating ? this.chargetype?.price : null
  }

  get pitchesToSell() {
    return this.chargetype?.pitchesToSell
  }

  get isLowAvailability() {
    return this.pitchesToSell < 10
  }

  get shouldShowNotifyMe() {
    return (
      this.hasChargeType &&
      this.chargetype.isNotifiable &&
      this.chargetype.state !== 'min_nights' &&
      !this.onlyArriveSelected &&
      !this.alternativeStaysLoading
    )
  }

  get shouldShowExtras() {
    return (
      this.hasChargeType &&
      !this.onlyArriveSelected &&
      this.chargetype.extras &&
      this.chargetype.extras.length
    )
  }

  get isAvailable() {
    return this.chargetype?.state === 'available'
  }

  get pitchNotAvailable() {
    return (
      ['max_capacity', 'adults_only'].includes(this.chargetype?.state) ||
      this.chargetype?.isOvercapacityMessage ||
      this.pitchtype?.chargetype?.isOvercapacityMessage
    )
  }

  @Watch('pitchNotAvailable')
  async onPitchNotAvailableChange() {
    this.syncShouldShowAvailabilityDetails()
    await this.scrollAvailabilitySectionIntoView()
  }

  syncShouldShowAvailabilityDetails() {
    if (this.isBookingAvailable) {
      this.shouldShowAvailabilityDetails = true
      return
    }
    if (this.hasChargeType && this.pitchNotAvailable) {
      this.shouldShowAvailabilityDetails = false
    }
  }

  get isBookingAvailable() {
    return (
      !this.chargeTypesUpdating && !this.onlyArriveSelected && this.isAvailable
    )
  }

  get pitchtypeBookingLinkProps() {
    return {
      pitchtypeName: this.pitchtype?.name,
      pitchtypeId: this.chargetype?.pitchtypeId || '',
      campsiteSlug: this.campsite.slug,
      categoryIds: this.categoryIds,
      dateRange: this.dateRange,
      chargetypeId: this.chargetype?.chargetypeId || '',
      party: this.party,
      disabled: !this.isBookingAvailable || this.onlyArriveSelected,
    }
  }

  get costsBreakdownCampsiteData() {
    return {
      name: this.campsite.name,
      slug: this.campsite.slug,
      allowOverrideImmediate: this.campsite.paymentInfo.allowOverrideImmediate,
      paymentTiming: this.campsite.paymentTiming,
      refundPolicy: this.campsite.refundPolicy,
      usefulInfo: this.campsite.usefulInfo,
      termsUsefulInfo: this.campsite.termsUsefulInfo,
      cancellationPolicy: this.campsite.policies,
    }
  }

  get offersActive() {
    return this.chargetype?.quote?.offersActive
  }

  get offerPercentage() {
    return this.chargetype?.quote?.offersApplied[0]?.discountPercent || 0
  }

  get bookingLinkParams() {
    return {
      campsiteSlug: this.campsite.slug,
      dateRange: this.dateRange,
      chargetypeId: this.chargetype?.chargetypeId,
      pitchtypeId: this.chargetype?.pitchtypeId,
      party: this.party,
    }
  }

  get showOffersLosenge() {
    return this.pitchtype?.activeOffers.length > 0
  }

  get partyValidation() {
    if (!this.party || !this.pitchtype) {
      return {
        isOverCapacity: false,
        isAvailable: true,
        violation: 'none' as const,
      }
    }
    // Convert Party to SearchParty format
    const searchParty = { ...this.party, dogs: this.party.dogs || 0 }

    return validatePitchtypeCapacity(searchParty, this.pitchtype, this.campsite)
  }

  @Watch('pitchtype', { immediate: true })
  toggleNoScroll(value) {
    if (process.client) {
      if (value) {
        document.body.setAttribute('data-pt-open', 'true')
      } else {
        document.body.removeAttribute('data-pt-open')
      }
    }
  }

  beforeUnmount() {
    if (this && process.client && document && document.body) {
      document.body.removeAttribute('data-pt-open')
    }
  }

  ptBodyScrolled = false
  headerVisibilityChanged(isVisible) {
    this.ptBodyScrolled = !isVisible
  }

  alternativeStaysData: CalendarDay[] = []
  alternativeStaysLoading = true
  alternativeStaysError: Error | null = null
  lastFetchParams: Record<string, string> | null = null

  provideAlternativeStays() {
    provide('alternativeStays', {
      data: computed(() => this.alternativeStaysData),
      loading: computed(() => this.alternativeStaysLoading),
      error: computed(() => this.alternativeStaysError),
    })
  }

  @Watch('chargetype', { deep: true })
  async onChargetypeChange() {
    await this.checkChargetypeState()
  }

  async checkChargetypeState() {
    if (
      this.chargetype?.state === 'unavailable' &&
      !this.chargetype?.isOvercapacityMessage
    ) {
      await this.fetchAlternativeStaysData()
    } else {
      this.alternativeStaysLoading = false
      this.lastFetchParams = null
      this.alternativeStaysData = []
    }
  }

  async fetchAlternativeStaysData() {
    const currentParams = {
      pitchtypeId: this.pitchtype.id,
      start: this.start,
      end: this.end,
      adults: String(this.$route.query.adults || '2'),
      childAges: String(this.$route.query.child_ages || ''),
      lang: this.$route.params.lang || 'en-gb',
    }
    // Check if params have changed
    if (isEqual(this.lastFetchParams, currentParams)) {
      logger('Skipping alternativeStaysData fetch - params unchanged')
      return
    }
    this.lastFetchParams = currentParams
    logger('fetching alternativeStaysData', currentParams)
    this.alternativeStaysLoading = true
    this.alternativeStaysError = null
    try {
      this.alternativeStaysData = await fetchAlternativeStays(currentParams)
      logger('alternativeStaysData fetched', this.alternativeStaysData)
    } catch (error) {
      this.alternativeStaysError = error as Error
    } finally {
      this.alternativeStaysLoading = false
    }
  }
}

export default toNative(CampsitePitchtypeDetailMain)
</script>
<style>
[data-pt-open] .app-header {
  display: none;
}

[data-pt-open] {
  overflow: hidden;
}

/* iOS26 cover underlying CS page behind dynamic island when PTD open */
[data-pt-open] .pitchtype-details:not(.desktop)::before {
  content: '';
  display: block;
  background: #ffffff;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 11;
  top: -10%;
  bottom: 0;
  left: 0;
  right: 0;
}
/* black background when PTD fullscreen gallery open */
[data-pt-open][data-flickity-fullscreen]
  .pitchtype-details:not(.desktop)::before {
  background: black;
}
/* black background when Reviews galleries open */
html.is-flickity-fullscreen .campsite-reviews:not(.desktop)::before {
  content: '';
  display: block;
  background: black;
  width: 100%;
  height: 100%;
  position: absolute;
  top: -10%;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}
</style>
<style scoped>
.pitchtype-details {
  height: 100%;
}

.pitchtype-details :deep(.pt-details) {
  position: relative;
  margin: 0;
}

.pitchtype-details :deep(.pt-status-msg) {
  align-items: center;
  background-color: #000;
  border-radius: 1em;
  color: white;
  display: flex;
  font-size: var(--small);
  font-weight: bold;
  justify-items: flex-start;
  margin: 0 0 0 1em;
  min-width: 14ch;
  padding: 0.2em 0.5em;
  white-space: normal;
}

.pitchtype-details :deep(.pt-status-msg img) {
  align-self: flex-start;
  height: 1.1em;
  margin-right: 0.25em;
  width: auto;
}

.pitchtype-details :deep(.pt-status-msg.other-dates) {
  background-color: var(--colour-price);
}

.pitchtype-details :deep(.pt-row.name) {
  align-items: start;
  margin: 0;
}

.pitchtype-details :deep(.pitchtype-name) {
  color: var(--colour-dark-blue);
  font-size: var(--body);
  font-weight: normal;
  line-height: 1.1;
  margin: 0.2em 0 0.3em;
}

.pitchtype-details :deep(.pt-details-state) {
  align-self: flex-end;
  flex-shrink: 1;
  margin: 0 0 0.3em 0.5em;
}

.pitchtype-details :deep(.pt-row) {
  margin-right: 0;
}

.pitchtype-details :deep(.pitchtype-categories.category-icons) {
  font-size: 0.9em;
}

.pt-wrapper {
  background-color: #fff;
  bottom: 0;
  display: flex;
  flex-direction: column;
  max-height: 100%; /* Fallback for safari 15 and below */
  height: 100dvh;
  left: 0;
  margin: 0 auto;
  overflow: hidden;
  padding: 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  z-index: 12;
}

.pt-header {
  border-bottom: 1px solid #dedede;
  border-radius: 0;
  box-shadow: 0 0 0.5em rgba(0, 0, 0, 0.4);
  padding: 0.3em 0.6em 0 0.7em;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  z-index: 1;
}

.pt-body {
  height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
  padding: 0 1em 1em 1em;
}

.pt-body:after {
  display: block;
  content: '';
  width: auto;
  height: 4em;
  background-color: #eef2f7;
  margin: 0 -1em;
}

.pt-footer {
  align-items: stretch;
  background-color: white;
  bottom: 0;
  box-shadow: 0 0 0.5em rgb(0 0 0 / 40%);
  display: flex;
  flex-direction: row;
  height: auto;
  justify-content: space-evenly;
  left: 0;
  margin: auto;
  padding: 0.4em 1.1em 0.9em;
  position: fixed;
  right: 0;
  top: auto;
  width: 100%;
  z-index: 9;
}

.footer-action-button {
  width: 100%;
}

.footer-action-button.disabled {
  background: var(--colour-page-background);
  box-shadow: 0 0.175em var(--colour-page-background);
  pointer-events: initial;
  color: white;
}

.pt-footer :deep(.button) {
  padding: 0.5em 0.9em 0.3em;
}

.pt-footer :deep(button + button) {
  margin: 0;
}

.close-btn {
  margin-right: 1.5em;
  border-color: var(--colour-light-grey);
  color: var(--text-default);
  display: flex;
  align-items: center;
}

.pt-notify-me {
  background: #eef2f7;
}

.pitchtype-details.unavailable :deep(.day),
.pitchtype-details.unavailable :deep(.flatpickr-day) {
  background-color: #b8c2cc;
  color: var(--colour-dark-blue);
  text-decoration: line-through;
}

.pitchtype-details.unavailable :deep(.day.selected),
.pitchtype-details.unavailable :deep(.flatpickr-day.selected),
.pitchtype-details.unavailable :deep(.flatpickr-day.inRange),
.pitchtype-details.unavailable :deep(.flatpickr-day.endRange) {
  background-color: #8e97a0;
}

.pitchtype-details.unavailable :deep(.key),
.pitchtype-details.unavailable :deep(.quote-price),
.pitchtype-details.unavailable :deep(.quote-nights) {
  display: none;
}

.pitchtype-details.min-nights :deep(.day.selected),
.pitchtype-details.min-nights :deep(.flatpickr-day.selected),
.pitchtype-details.min-nights :deep(.flatpickr-day.inRange),
.pitchtype-details.min-nights :deep(.flatpickr-day.endRange) {
  background-color: var(--colour-red);
  box-shadow:
    inset 0 2px 0 0 var(--colour-red),
    inset 0 -2px 0 0 var(--colour-red);
}

.availability-error-state {
  display: flex;
  align-items: center;
  background-color: #eef2f7;
  margin: 0 -1em 0 -1em;
  padding: 1em 1em 0;
  background: #eef2f7;
}

.availability-error-state :deep(span) {
  color: var(--colour-red);
  font-weight: bold;
}

.availability-error-state :deep(img) {
  margin-right: 0.4em;
  align-self: flex-start;
}

.zero-availability {
  background: #eef2f7;
  padding: 1.2em 1em;
  margin: 0 -1em 0 -1em;
}

.zero-availability p {
  background: white;
  border-radius: 0.5em;
  padding: 0.8em;
  color: var(--colour-dark-blue);
}

.zero-availability p a {
  color: var(--colour-dark-blue);
}

.nearby-link {
  display: block;
  margin-top: 0.7em;
}

.undated-extras-accordion {
  background-color: #eef2f7;
  margin: 0 -1em;
  padding: 1em;
}

.undated-extras-accordion :deep(.ui-collapse) {
  background: white;
  border-radius: 0.5em;
  border: none;
}

.undated-extras-accordion :deep(.ui-collapse-title) {
  font-size: var(--body);
  color: var(--colour-dark-blue);
}

.undated-extras-accordion :deep(.ui-collapse-content) {
  border-top: none;
}

.undated-extras-accordion :deep(.extras) {
  border-radius: 0 0 0.5em 0.5em;
}

.undated-extras-accordion :deep(.extras h4) {
  display: none;
}

.undated-extras-accordion :deep(.extras-table) {
  margin-top: 0;
}

.undated-extras-accordion :deep(.ui-collapse-header) {
  min-height: 2.5em;
  padding: 0.25em 0.8em;
}

.undated-extras-accordion :deep(.extras-wrapper) {
  padding: 0 1em 1em 1em;
}

/* desktop & tablet */
.pitchtype-details.desktop :deep(.pt-row) {
  justify-content: space-between;
}

.pitchtype-details.desktop :deep(.pt-status-msg) {
  min-width: 10ch;
}

.pitchtype-details.desktop :deep(.facilities) {
  font-size: inherit;
  columns: 16ch 3;
  display: block;
  column-gap: 2em;
}

.pitchtype-details.desktop :deep(.facilities .row) {
  break-inside: avoid;
}

.desktop .pt-header {
  font-size: 1.5em;
  box-shadow: none;
  border: none;
  padding: 0;
  position: relative;
  transition: box-shadow 0.2s ease-out;
}

.desktop .pt-header.scrolled {
  box-shadow: 0 0 0.5em rgba(0, 0, 0, 0.4);
}

.desktop .pt-header :deep(.back-btn) {
  position: absolute;
  margin: 1.1em auto 0 calc(var(--page-width-padding) / 2);
  height: auto;
  width: var(--page-width-padding);
  min-width: 0.5em;
}

.desktop .pt-header :deep(.back-btn img) {
  width: 0.9em;
}

.desktop .pt-header :deep(.pt-summary) {
  display: block;
  padding: 0.5em calc(var(--page-width-padding) * 2) 0;
}

.desktop .pt-header :deep(.dated-status),
.desktop .pt-header :deep(.undated-status) {
  margin: 0;
}

.desktop .pt-header :deep(.dated-status) {
  column-gap: 2em;
  margin-bottom: 0.25em;
}

.desktop .pt-body {
  padding: 1em calc(var(--page-width-padding) * 2) 2em;
  height: auto;
  flex-shrink: 1;
}

.desktop .pt-body:after {
  content: none;
}

.desktop .dated-extras.extras-wrapper {
  background-color: #eef2f7;
  margin: 0 -1em;
  padding: 1em;
  border-radius: 0.6em;
}

.desktop .dated-extras :deep(.extras) {
  background: white;
  border-radius: 0.5em;
  padding: 0.2em 0.8em 0.5em;
}

.desktop .pt-footer {
  align-items: flex-start;
  column-gap: 2.5em;
  display: grid;
  flex-grow: 1;
  grid-template-columns: 1fr 1fr 1.25fr;
  grid-template-rows: auto;
  justify-items: left;
  padding: 0.5em calc(var(--page-width-padding) * 2);
  position: relative;
}

.desktop .pt-footer :deep(.footer-action-button) {
  width: calc(100% - 2.8em);
  grid-column: 3/4;
  justify-self: right;
}

.desktop .pt-footer :deep(.button.primary) {
  font-size: 1.2em;
  margin-bottom: 0.5em;
}

.desktop .close-btn {
  margin-top: 0.3em;
}

.desktop.date-range-selected .close-btn {
  margin-top: 1em;
}

.desktop .zero-availability {
  margin: 0;
}

.desktop .availability-error-state {
  margin: 0;
  padding: 1em 1.4em;
}

.desktop.pitch-type-detail :deep(.costs-breakdown) {
  margin: 0;
  padding: 0.5em 1.4em 5em;
  border-radius: 0 0 0.6em 0.6em;
}
</style>
